import React, { useContext, useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { useFetchApi } from "../../hooks/useFetchApi";
import {
  convertToInternationalCurrencySystem,
  currencyDecimalMap,
} from "../../helper";
import Badge from "../../components/Badge";
import Button from "../../components/Button";
import Caption from "../../components/Typography/Caption";
import { GRAPH_TYPES } from "../../store/types";
import Modal from "../../components/Modal";
import H4 from "../../components/Typography/H4";
import { StateContext } from "../../store/context";
import Entitlement, { isAllowed } from "../../components/Entitlements";
import { KEYS } from "../../setup/entitlements";
import { format, subMonths } from "date-fns";
import { TitleArea } from "../../components/Typography/titleArea";
import { StockSelectOptions } from "./components/stockSelectOptions";
import { ParameterSelector } from "./components/selectParameters";
import { parametersDB } from "./components/parametersDb";
import { PeriodSelector } from "./components/periodSelector";
import { ChartTabs } from "./components/chartTabs";
import {
  FaChevronLeft,
  FaChevronRight,
  FaSquareRootAlt,
  FaPlus,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import lodash from "lodash";
import { ChartTypeSelector } from "./components/ChartTypeSelector";
import { DecimalSelector } from "./components/Decimalselector";
import DisclaimerButton from "../../components/DisclaimerButton";
import { useScrollPin } from "../../hooks/useScrollPin";
import {
  mergeDgChartOptions,
  mergeDgBarChartOptions,
  mergeDgAreaChartOptions,
} from "./components/chartTheme";
import "../../styles/theme-tokens.css";
import "../../styles/glass-tokens.css";
import "./dynamicGraphs.theme.css";

/** Pick the page-local chartTheme.ts merge helper matching a chart's
 *  currently-selected type (the type itself is user-toggleable per
 *  chart via ChartTypeSelector, so this can't be decided once). */
const mergeFnForType = (type: "line" | "area" | "bar") =>
  type === "bar"
    ? mergeDgBarChartOptions
    : type === "area"
      ? mergeDgAreaChartOptions
      : mergeDgChartOptions;

const NewDynamicGraphs: React.FC = (): React.ReactElement => {
  const { baseCurrency, authUser } = useContext(StateContext);
  const { allowed = [], role_name, sub_plan } = authUser;
  const [selectedMetrics, setSelectedMetrics] = useState<Array<any>>([]);
  const [startDate, setStartDate] = useState(
    format(subMonths(new Date(), 6), "yyyy-MM-dd"),
  );
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [allPeriod, setAllPeriods] = useState<any>([1, 3, 5]);

  const [absoluteValueChartData, setAbsoluteValueChartData] = useState<any>({});
  const [percentageValueChartData, setPercentageValueChartData] = useState<any>(
    {},
  );
  const [timesValueChartData, setTimesValueChartData] = useState<any>({});
  const [multipleValueChartData, setMultipleValueChartData] = useState<any>({});
  const [tabs, setTabs] = useState<any>([]);
  const [graphInfo, setGraphInfo] = useState<any>({});
  const [decimalPlaces, setDecimalPlaces] = useState<number>(
    currencyDecimalMap[baseCurrency] || 2,
  );

  const [percentageChartType, setPercentageChartType] = useState<
    "line" | "area" | "bar"
  >("area");
  const [absoluteChartType, setAbsoluteChartType] = useState<
    "line" | "area" | "bar"
  >("area");
  const [daysChartType, setDaysChartType] = useState<"line" | "area" | "bar">(
    "area",
  );
  const [multiplesChartType, setMultiplesChartType] = useState<
    "line" | "area" | "bar"
  >("area");

  // Changing chart type / decimals / interval / period reloads the
  // graph data (see the effect below) — the loading placeholder and
  // then the real chart briefly resize the scroll container, which
  // jumps the page. pinScroll() holds the scroll position through that.
  const { containerRef: scrollPinRef, pinScroll } =
    useScrollPin<HTMLDivElement>();

  const navigate = useNavigate();

  const {
    response: allStocks,
    loading: stockLoading,
    execute: getStocks,
  } = useFetchApi("/api/all-stocks", {}, "GET");

  const {
    execute: generateGraphs,
    loading: graphLoading,
    response: graphData,
  } = useFetchApi("/api/dynamic-graph");

  const {
    execute: saveGraphSnapshot,
    loading: saveGraphSnapshotLoading,
    response: saveGraphSnapshotResponse,
  } = useFetchApi("/api/save-graph", {}, "POST", () => setGraphInfo({}));

  const {
    response: graphCategories,
    loading: graphCategoriesLoading,
    execute: getGraphCategories,
  } = useFetchApi("/api/graph-categories", {}, "GET");

  const {
    response: metricsAndEquations,
    loading: metricsAndEquationsLoading,
    execute: getMetricsAndEquations,
  } = useFetchApi("/api/metrics-and-equations", {}, "GET");

  const checkForPeriodEligibility = () => {
    if (isAllowed([KEYS.ENABLE_DYNAMIC_GRAPH_7_YEAR_PERIOD], allowed)) {
      setAllPeriods([...allPeriod, 7]);
    }
    if (isAllowed([KEYS.ENABLE_DYNAMIC_GRAPH_10_YEAR_PERIOD], allowed)) {
      setAllPeriods([...allPeriod, 10]);
    }
  };

  useEffect(() => {
    getStocks();
    // getMetrics();
    // getEquations();
    getGraphCategories();
    getMetricsAndEquations();
    checkForPeriodEligibility();
  }, []);

  useEffect(() => {
    setDecimalPlaces(currencyDecimalMap[baseCurrency] || 2);
  }, [baseCurrency]);

  useEffect(() => {
    if (graphData && Object.keys(graphData).length) {
      graphData.series.map((s: any) => {
        console.log("S:", s);
        if (s.metricGraphType === GRAPH_TYPES.PERCENTAGES) {
          console.log("PERCENTAGES:", "PERCENTAGES");
          setPercentageValueChartData((prev: any) => ({
            series: [...(prev?.series ? prev?.series : []), s],
            options: mergeFnForType(percentageChartType)({
              chart: {
                type: percentageChartType,
                id: "dynamic-graph-percentage",
              },
              dataLabels: {
                enabled: true,
                formatter: (value: any) => {
                  if (value === 0) return value;
                  if (value) {
                    return `${Number(value).toFixed(2)}%`;
                  }
                },
                offsetY: percentageChartType === "bar" ? -20 : -10,
              },
              yaxis: {
                labels: {
                  formatter: (value: any) => {
                    if (value === null || value === undefined) return "";
                    return `${Number(value).toFixed(2)}%`;
                  },
                },
              },
              xaxis: {
                categories: graphData.categories,
              },
              tooltip: {
                enabled: true,
                hideEmptySeries: false,
                y: {
                  formatter: (value: any) => {
                    if (value === null || value === undefined || isNaN(value))
                      return "N/A";
                    return `${Number(value).toFixed(2)}%`;
                  },
                },
              },
              legend: {
                showForSingleSeries: true,
              },
            }),
          }));
        }

        if (s.metricGraphType === GRAPH_TYPES.ABSOLUTE_VALUES) {
          const allAnnotations: any = [];

          const allSeriesData = graphData.series;

          allSeriesData.forEach((series: any) => {
            if (series.shareSplits) {
              const seriesAnnotations = series.shareSplits.map(
                (split: any) => ({
                  x: split.date,
                  borderColor: "#4aa8ff",
                  label: {
                    position: "bottom",
                    style: {
                      color: "#071220",
                      background: "#4aa8ff",
                    },
                    //text: `Split Ratio: ${split.splitRatio}`,
                  },
                }),
              );
              allAnnotations.push(...seriesAnnotations);
            }
          });

          console.log("ABSOLUTE_VALUES:", "ABSOLUTE_VALUES");
          setAbsoluteValueChartData((prev: any) => ({
            series: [...(prev?.series ? prev?.series : []), s],
            options: mergeFnForType(absoluteChartType)({
              chart: {
                type: absoluteChartType,
                id: "dynamic-graph-absolute",
              },
              dataLabels: {
                enabled: true,
                formatter: (value: any) => {
                  if (value == 0) return value;
                  if (value) {
                    return convertToInternationalCurrencySystem(
                      value,
                      decimalPlaces,
                    );
                  }
                },
                offsetY: absoluteChartType === "bar" ? -20 : -10,
              },
              yaxis: {
                labels: {
                  formatter: (value: any) => {
                    if (
                      value === null ||
                      value === undefined ||
                      isNaN(parseFloat(value))
                    )
                      return "";
                    return `${baseCurrency} ${convertToInternationalCurrencySystem(
                      value,
                      decimalPlaces,
                    )}`;
                  },
                },
              },
              xaxis: {
                categories: graphData.categories,
              },
              tooltip: {
                y: {
                  formatter: function (val: any, opts: any) {
                    if (val === null || val === undefined || isNaN(val)) {
                      return "N/A";
                    }

                    const xValue =
                      opts.w.globals.categoryLabels[opts.dataPointIndex];
                    const seriesName =
                      opts.w.globals.seriesNames[opts.seriesIndex];

                    // Find share split from the current chart data by matching series name
                    const allSeries = opts.w.config.series;
                    const currentSeries = allSeries.find(
                      (series: any) => series.name === seriesName,
                    );

                    // Find if there's a share split for this date in the current series
                    const shareSplit = currentSeries?.shareSplits?.find(
                      (split: any) => {
                        return (
                          split.date === xValue ||
                          new Date(split.date).toDateString() ===
                            new Date(xValue).toDateString()
                        );
                      },
                    );

                    let formattedValue = `${baseCurrency} ${convertToInternationalCurrencySystem(
                      val,
                      decimalPlaces,
                    )}`;

                    const getRatioType = (splitRatio: any) => {
                      const [first, second] = splitRatio.split(":").map(Number);
                      return first > second ? "Consolidation" : "Split";
                    };

                    if (shareSplit) {
                      const ratioType = getRatioType(shareSplit.splitRatio);
                      formattedValue += ` 📊 ${ratioType} Ratio: ${shareSplit.splitRatio}`;
                    }

                    return formattedValue;
                  },
                },
              },
              legend: {
                showForSingleSeries: true,
              },
              annotations: {
                xaxis: allAnnotations,
              },
            }),
          }));
        }

        if (s.metricGraphType === GRAPH_TYPES.DAYS) {
          console.log("DAYS:", "DAYS");

          setTimesValueChartData((prev: any) => ({
            series: [...(prev?.series ? prev?.series : []), s],
            options: mergeFnForType(daysChartType)({
              chart: {
                type: daysChartType,
                id: "dynamic-graph-days",
              },
              tooltip: {
                hideEmptySeries: false,
                y: {
                  formatter: (value: any) => {
                    if (value === null || value === undefined || isNaN(value))
                      return "N/A";
                    if (Number(value).toFixed(0) === "-0") return 0 + " days";
                    return Number(value).toFixed(0) + " days";
                  },
                },
              },
              dataLabels: {
                enabled: true,
                formatter: (value: any) => {
                  if (value == 0) return value;
                  if (Number(value).toFixed(0) === "-0") return 0 + " days";
                  if (value) {
                    return Number(value).toFixed(0) + " days";
                  }
                },
                offsetY: daysChartType === "bar" ? -20 : -10,
              },
              yaxis: {
                labels: {
                  formatter: (value: any) => {
                    return value;
                  },
                },
              },
              xaxis: {
                categories: graphData.categories,
              },
              legend: {
                showForSingleSeries: true,
              },
            }),
          }));
        }

        if (s.metricGraphType === GRAPH_TYPES.MULTIPLES) {
          console.log("MULTIPLES:", "MULTIPLES");

          setMultipleValueChartData((prev: any) => ({
            series: [...(prev?.series ? prev?.series : []), s],
            options: mergeFnForType(multiplesChartType)({
              chart: {
                type: multiplesChartType,
                id: "dynamic-graph-multiples",
              },
              dataLabels: {
                enabled: true,
                formatter: (value: any) => {
                  if (value == 0) return value;
                  if (value) {
                    return Number(value).toFixed(2) + "x";
                  }
                },
                offsetY: multiplesChartType === "bar" ? -20 : -10,
              },
              yaxis: {
                labels: {
                  formatter: (value: any) => {
                    if (value === null || value === undefined) return "";
                    return Number(value).toFixed(2) + "x";
                  },
                },
              },
              tooltip: {
                y: {
                  formatter: (val: any) => {
                    if (val === null || val === undefined || isNaN(val))
                      return "N/A";
                    return Number(val).toFixed(2) + "x";
                  },
                },
              },
              xaxis: {
                categories: graphData.categories,
              },
              legend: {
                showForSingleSeries: true,
              },
            }),
          }));
        }
      });
    }
  }, [graphData]);

  const handleSaveGraph = (e: any) => {
    const filteredMetrics = selectedMetrics.filter((m: any) => {
      if (m.type && m.type === e.target.id) {
        return m;
      } else if (!m.type && e.target.id === GRAPH_TYPES.ABSOLUTE_VALUES) {
        return m;
      }
    });
    setGraphInfo({
      selectedMetrics: filteredMetrics,
      graphType: selectedInterval,
      period,
      category_id: graphCategories[0].id,
    });
  };

  const saveGraph = () => {
    saveGraphSnapshot({
      graphName: graphInfo.graphName,
      graphData: JSON.stringify({
        selectedMetrics: graphInfo.selectedMetrics,
        graphType: selectedInterval,
        period,
      }),
      // order_no: 2,
      category_id: graphInfo.category_id,
    });
  };

  const AbsoluteGraph = () => (
    <div className="w-full vi-glass-panel p-3">
      <div className="flex items-center justify-end gap-4 m-2 mb-4">
        <ChartTypeSelector
          value={absoluteChartType}
          onChange={(v) => {
            pinScroll();
            setAbsoluteChartType(v);
          }}
        />
        <DecimalSelector
          value={decimalPlaces}
          onChange={(v) => {
            pinScroll();
            setDecimalPlaces(v);
          }}
        />
        <Entitlement allowedKeys={[KEYS.SAVE_DYNAMIC_GRAPH]}>
          <Button
            id={GRAPH_TYPES.ABSOLUTE_VALUES}
            variant="primary"
            size="small"
            onClick={handleSaveGraph}
          >
            Save Graph
          </Button>
        </Entitlement>
      </div>
      <Chart
        options={absoluteValueChartData.options}
        series={absoluteValueChartData.series}
        height={550}
        type={absoluteChartType}
      />
    </div>
  );

  const PercentageGraph = () => (
    <div className="w-full vi-glass-panel p-3">
      <div className="flex items-center justify-end gap-4 m-2 mb-4">
        <ChartTypeSelector
          value={percentageChartType}
          onChange={(v) => {
            pinScroll();
            setPercentageChartType(v);
          }}
        />
        <Entitlement allowedKeys={[KEYS.SAVE_DYNAMIC_GRAPH]}>
          <Button
            id={GRAPH_TYPES.PERCENTAGES}
            variant="primary"
            size="small"
            onClick={handleSaveGraph}
          >
            Save Graph
          </Button>
        </Entitlement>
      </div>
      <Chart
        options={percentageValueChartData.options}
        series={percentageValueChartData.series}
        height={550}
        type={percentageChartType}
      />
    </div>
  );

  const DayGraph = () => (
    <div className="w-full vi-glass-panel p-3">
      <div className="flex items-center justify-end gap-4 m-2 mb-4">
        <ChartTypeSelector
          value={daysChartType}
          onChange={(v) => {
            pinScroll();
            setDaysChartType(v);
          }}
        />
        <Entitlement allowedKeys={[KEYS.SAVE_DYNAMIC_GRAPH]}>
          <Button
            id={GRAPH_TYPES.DAYS}
            variant="primary"
            size="small"
            onClick={handleSaveGraph}
          >
            Save Graph
          </Button>
        </Entitlement>
      </div>
      <Chart
        options={timesValueChartData.options}
        series={timesValueChartData.series}
        height={550}
        type={daysChartType}
      />
    </div>
  );

  const MultipleGraph = () => (
    <div className="w-full vi-glass-panel p-3">
      <div className="flex items-center justify-end gap-4 m-2 mb-4">
        <ChartTypeSelector
          value={multiplesChartType}
          onChange={(v) => {
            pinScroll();
            setMultiplesChartType(v);
          }}
        />
        <Entitlement allowedKeys={[KEYS.SAVE_DYNAMIC_GRAPH]}>
          <Button
            id={GRAPH_TYPES.MULTIPLES}
            variant="primary"
            size="small"
            onClick={handleSaveGraph}
          >
            Save Graph
          </Button>
        </Entitlement>
      </div>
      <Chart
        options={multipleValueChartData.options}
        series={multipleValueChartData.series}
        height={550}
        type={multiplesChartType}
      />
    </div>
  );

  const EquationsPanel = () => (
    <div className="w-full vi-glass-panel p-3">
      <h3 className="vi-equations-title flex items-center gap-2 text-lg font-semibold px-2">
        <FaSquareRootAlt className="h-4 w-4" />
        Equations Used
      </h3>
      <ul className="vi-equations-list px-2 mt-3">
        {graphData?.equations_used?.map((item: any, index: number) => (
          <li key={index} className="vi-equations-item">
            <span className="vi-equations-name">{item.name}</span>
            <span className="vi-equations-eq">=</span>
            <code className="vi-equations-formula">{item.equation}</code>
          </li>
        ))}
      </ul>
    </div>
  );

  useEffect(() => {
    setTabs((prev: any) => []);

    if (percentageValueChartData && percentageValueChartData.series) {
      setTabs((prev: any) => [
        ...prev,
        { label: "Percentage Values", content: <PercentageGraph /> },
      ]);
    }

    if (absoluteValueChartData && absoluteValueChartData.series) {
      setTabs((prev: any) => [
        ...prev,
        { label: "Absolute Values", content: <AbsoluteGraph /> },
      ]);
    }

    if (timesValueChartData && timesValueChartData.series) {
      setTabs((prev: any) => [
        ...prev,
        { label: "Days", content: <DayGraph /> },
      ]);
    }

    if (multipleValueChartData && multipleValueChartData.series) {
      setTabs((prev: any) => [
        ...prev,
        { label: "Multiples", content: <MultipleGraph /> },
      ]);
    }

    // "Equations Used" now lives as a tab alongside the graph views
    // (was a separate glass-card block stacked below the tabs) so it
    // sits next to Percentage/Absolute Values instead of pushing the
    // rest of the page down.
    if (graphData?.equations_used?.length > 0) {
      setTabs((prev: any) => [
        ...prev,
        { label: "Equations Used", content: <EquationsPanel /> },
      ]);
    }
  }, [
    percentageValueChartData,
    absoluteValueChartData,
    timesValueChartData,
    multipleValueChartData,
    graphData,
  ]);
  const [selectedStocks, setSelectedStockList] = useState<any[]>([]);
  const [selectedInterval, setSelectedInterval] = useState<any>("quarterly");
  const [period, setSelectedPeriod] = useState("3");
  const handleRemoveStock = (stock: any) => {
    pinScroll();
    setSelectedStockList(selectedStocks.filter((s) => s.id !== stock.id));
  };
  const handleRemoveParameter = (parameter: any) => {
    pinScroll();
    setSelectedMetrics(
      selectedMetrics.filter((s) => s.name !== parameter.name),
    );
  };
  const handleParameterChange = (selectedMetrics: { name: string }[]) => {
    pinScroll();
    setSelectedMetrics(selectedMetrics);
  };

  useEffect(() => {
    if (selectedMetrics?.length && selectedStocks?.length) {
      setTabs([]);
      setAbsoluteValueChartData({});
      setPercentageValueChartData({});
      setTimesValueChartData({});
      setMultipleValueChartData({});
      setPercentageValueChartData({});
      generateGraphs({
        selectedMetrics,
        selectedStocks,
        filter: selectedInterval,
        period,
        baseCurrency,
        range: {
          start: startDate,
          end: endDate,
        },
        splitAdjustedMetrics: ["Revenue per Share"],
      });
    } else {
      setTabs([]);
    }
  }, [
    selectedMetrics,
    selectedStocks,
    period,
    selectedInterval,
    baseCurrency,
    percentageChartType,
    absoluteChartType,
    daysChartType,
    multiplesChartType,
    decimalPlaces,
  ]);

  const [isRightSectionVisible, setIsRightSectionVisible] = useState(true);
  const [mobileParameterModalOpen, setMobileParameterModalOpen] =
    useState(false);

  const toggleRightSection = () => {
    setIsRightSectionVisible(!isRightSectionVisible);
  };

  const formatLabel = (key: string) => {
    if (key[0] === key[0].toUpperCase()) {
      return key;
    }

    return lodash.startCase(key);
  };

  const isBasicCustomer =
    sub_plan === "Free Plan" && role_name === "Basic Customer";
  return (
    <div className="vi-dg vi-glass-sheen my_container">
      <div
        className={`left-section !h-[98vh] overflow-y-auto !flex-col select-none ${
          !isRightSectionVisible ? "flex-1" : ""
        }`}
      >
        <div className="title-area">
          <div className="w-full flex items-start justify-between gap-4">
            <TitleArea
              variant="page"
              title="Dynamic Graphs"
              subTitle="Analyze key financial metrics and ratios of multiple stocks through customizable, interactive graphs"
            />
            <DisclaimerButton />
          </div>
        </div>
        <hr className="hr-line" />

        <div className="hidden mb-4">
          <ParameterSelector
            metricsAndEquations={metricsAndEquations}
            onChange={handleParameterChange}
            selectedMetrics={selectedMetrics}
          />
        </div>

        <Modal
          onClose={() => setGraphInfo({})}
          onSubmit={saveGraph}
          status={graphInfo && Object.keys(graphInfo).length}
          size="small"
        >
          <div className="flex flex-col w-full !overflow-x-hidden">
            <div className="flex items-center justify-between mb-4">
              <H4 className="text-[color:var(--vi-text)] font-dm">Save Graph</H4>
            </div>
            <div className="flex w-full flex-col !overflow-x-hidden p-4">
              <div className="flex flex-col mb-4">
                <Caption className="mb-2 text-[color:var(--vi-text-secondary)] font-dm">
                  Metrics Selected
                </Caption>
                {graphInfo && Object.keys(graphInfo).length && (
                  <>
                    {graphInfo.selectedMetrics.map((metric: any) => (
                      // <li key={metric.id} className="font-semibold"></li>
                      <Badge small status="success">
                        {metric.name}
                      </Badge>
                    ))}
                  </>
                )}
              </div>
              <div className="flex flex-col mb-4">
                <Caption className="mb-2 text-[color:var(--vi-text-secondary)] font-dm">
                  Period: {graphInfo.period} year(s)
                </Caption>
              </div>
              <div className="flex flex-col mb-4">
                <Caption className="mb-2 text-[color:var(--vi-text-secondary)] font-dm">
                  Filter: {graphInfo.graphType}
                </Caption>
              </div>
              <div className="flex flex-col mb-4">
                <Caption className="mb-2 text-[color:var(--vi-text-secondary)] font-dm">
                  Graph Name
                </Caption>
                <input
                  className="!w-full !font-inter  !outline-none focus:!outline-none !border-[#D0D5DD] !h-[40px] !rounded-[8px] !border focus:!border-none placeholder:!text-[#667085]"
                  name="graphName"
                  onChange={(e) => {
                    setGraphInfo((prev: any) => ({
                      ...prev,
                      graphName: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="flex flex-col mb-4">
                <Caption className="mb-2 text-[color:var(--vi-text-secondary)] font-dm">
                  Category
                </Caption>
                {graphCategories && graphCategories.length ? (
                  <select
                    className="!w-full !font-inter  !outline-none focus:!outline-none !border-[#D0D5DD] !h-[40px] !rounded-[8px] !border focus:!border-none placeholder:!text-[#667085]"
                    name="category"
                    onChange={(e) => {
                      setGraphInfo((prev: any) => ({
                        ...prev,
                        category_id: e.target.value,
                      }));
                    }}
                  >
                    {graphCategories.map((category: any) => (
                      <option value={category.id}>{category.name}</option>
                    ))}
                  </select>
                ) : null}
              </div>
            </div>
          </div>
        </Modal>

        {mobileParameterModalOpen && (
          <div className="fixed inset-0 z-50 flex w-full !h-full px-4 pt-4 pb-4  items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg w-full   !h-full max-w-md shadow-lg flex flex-col max-h-[80vh]">
              <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-lg font-semibold text-[color:var(--vi-text)] px-2">
                  Parameter
                </h3>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="flex-1 overflow-y-auto px-4 py-4 !h-full ">
                <ParameterSelector
                  metricsAndEquations={metricsAndEquations || parametersDB}
                  onChange={handleParameterChange}
                  selectedMetrics={selectedMetrics}
                />
              </div>

              {/* Modal Footer - Fixed */}
              <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-x-3 flex-shrink-0 mb-10 w-full">
                <div
                  onClick={() => setMobileParameterModalOpen(false)}
                  className="!w-full !bg-white !border !border-[#D0D5DD] !flex !items-center !justify-center !text-[#344054] rounded-full !h-9"
                >
                  Cancel
                </div>
                <div
                  onClick={() => setMobileParameterModalOpen(false)}
                  className="!w-full !border !border-transparent !flex !items-center !justify-center rounded-full !h-9 text-[color:var(--vi-accent-on)] font-semibold"
                  style={{
                    background: "var(--vi-accent-grad)",
                  }}
                >
                  Confirm
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Unified controls bar — was two separate rows (a stock-search
            box + desktop-only "Selected Parameter" box, then a full
            second row below for Interval/Period, plus a THIRD mobile-
            only parameter block) stacked on top of each other, pushing
            the actual chart section down. All four controls (Stocks,
            Interval, Period, Parameters) now live in one compact row —
            chips instead of an always-visible search box for both
            Stocks and Parameters, a small "+ Add" trigger opening the
            existing stock-search popup / parameter panel respectively,
            same content just condensed instead of duplicated per
            breakpoint. */}
        <div
          ref={scrollPinRef}
          className="vi-glass-card p-3 flex flex-wrap items-start gap-x-6 gap-y-4"
        >
          {/* Stocks — search bar always first/on top, chips (when any are
              selected) below it: as more stocks get picked and the chips
              wrap onto more lines, the search bar stays put at the top
              instead of getting pushed further down each time. */}
          <div className="flex flex-col gap-2 flex-1 min-w-[220px]">
            <span className="text-xs font-semibold uppercase tracking-wide text-[color:var(--vi-text-tertiary)] font-inter">
              Stocks
            </span>
            <StockSelectOptions
              selectedStocks={selectedStocks}
              setSelectedSotcks={(stocks) => {
                pinScroll();
                setSelectedStockList(stocks);
              }}
            />
            {selectedStocks?.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                {selectedStocks.map((stock: any) => (
                  <div key={stock.id} className="stock-item vi-glass-chip vi-dg-chip-sm">
                    <img
                      src={`/${stock.company_logo}`}
                      alt=""
                      className="w-4 h-4 object-contain"
                    />
                    {/* Ticker symbol, not the full company name — keeps
                        these chips compact. */}
                    <span>{stock.ticker_symbol}</span>
                    <button onClick={() => handleRemoveStock(stock)}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="hidden md:block w-px self-stretch bg-[color:var(--vi-card-border)]" />

          {/* Interval + Period — periodSelector.tsx already lays these two
              out side by side */}
          <PeriodSelector
            selectedInterval={selectedInterval}
            setSelectedInterval={(v: any) => {
              pinScroll();
              setSelectedInterval(v);
            }}
            selectedPeriod={period}
            setSelectedPeriod={(v: any) => {
              pinScroll();
              setSelectedPeriod(v);
            }}
          />

          <div className="hidden md:block w-px self-stretch bg-[color:var(--vi-card-border)]" />

          {/* Parameters — the actual metric picker is the existing
              right-side sliding panel (desktop, opened via its own
              chevron toggle elsewhere on the page — no separate trigger
              needed here) / full-screen modal (mobile, which has no
              other entry point, so it keeps its own "+ Add" here). */}
          <div className="flex flex-col gap-2 flex-1 min-w-[220px]">
            <span className="text-xs font-semibold uppercase tracking-wide text-[color:var(--vi-text-tertiary)] font-inter">
              Parameters
            </span>
            <div className="flex flex-wrap items-center gap-1">
              {selectedMetrics?.map((stock) => (
                <div key={stock.name} className="stock-item vi-glass-chip vi-dg-chip-sm">
                  <span>{formatLabel(stock.name)}</span>
                  <button onClick={() => handleRemoveParameter(stock)}>
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setMobileParameterModalOpen(true)}
                className="vi-dg-pill md:hidden flex items-center gap-1.5 !h-8 px-3 text-xs font-medium whitespace-nowrap"
              >
                <FaPlus className="h-2.5 w-2.5" />
                Add
              </button>
            </div>
          </div>
        </div>
        <div className="w-full flex-col border-t-[1px] !border-[#D0D5DD] mt-4">
          <div className="mt-4 ">
            <ChartTabs
              tabs={tabs}
              hasData={tabs?.length > 0 && selectedMetrics?.length > 0}
              loading={graphLoading}
            />
          </div>

        </div>
        {/* </div> */}
      </div>

      <div className="relative hidden md:block">
        <button
          onClick={toggleRightSection}
          className="vi-dg-toggle vi-glass-button absolute -left-4 top-1/2 transform -translate-y-1/2 rounded-full p-1 transition-colors z-10"
        >
          {isRightSectionVisible ? (
            <FaChevronRight className="h-4 w-4" />
          ) : (
            <FaChevronLeft className="h-4 w-4" />
          )}
        </button>
        <div
          className={`vi-dg-param-panel vi-glass-panel transition-all duration-300 !hidden md:!block !p-0 h-full ${
            isRightSectionVisible ? "w-[300px]" : "w-0 overflow-hidden"
          }`}
        >
          <ParameterSelector
            metricsAndEquations={metricsAndEquations || parametersDB}
            onChange={handleParameterChange}
            selectedMetrics={selectedMetrics}
          />
        </div>
      </div>
      {/* </div> */}
    </div>
  );
};

export default NewDynamicGraphs;
